import { useCallback, useEffect, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { RANGE_PICKER } from '@nocowanie/core/consts';
import { dateHelpers } from '@nocowanie/core/helpers';

import { RangePickerConfig } from './range-picker.config';
import { RangePickerProps } from './range-picker.props';

import { getTooltipText } from '../../../helpers/range-picker.helpers';

const defaultBounds = {
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    bottom: 0,
    right: 0,
};

export const useRangePickerPopper = ({
    dateRange,
    daysConfig,
    isMobile = false,
    translationData,
}: {
    daysConfig: RangePickerProps['daysConfig'] | null;
    dateRange: {
        startDate: Date | null;
        endDate: Date | null;
    };
    translationData: RangePickerConfig['translationData'];
    isMobile?: boolean;
}) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [tooltipText, setTooltipText] = useState<string | undefined>(undefined);
    const [autoCloseTimeout, setAutoCloseTimeout] = useState<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const [referenceBounds, setReferenceBounds] = useState({ ...defaultBounds });

    const onClose = useCallback(() => {
        if (!isPopoverOpen) {
            return;
        }

        setTimeout(() => {
            setIsPopoverOpen(false);
            setReferenceBounds({ ...defaultBounds });
            setTooltipText(undefined);

            if (isMobile && autoCloseTimeout !== null) {
                clearTimeout(autoCloseTimeout);
                setAutoCloseTimeout(null);
            }
        }, 0);
    }, [autoCloseTimeout, isMobile, isPopoverOpen]);

    useEffect(() => {
        if (!isPopoverOpen || !isMobile) {
            autoCloseTimeout && clearTimeout(autoCloseTimeout);
            return;
        }

        if (autoCloseTimeout !== null) {
            return;
        }

        const timeout = setTimeout(() => {
            onClose();
        }, RANGE_PICKER.POPPER_CLOSE_DELAY);

        setAutoCloseTimeout(timeout);
    }, [autoCloseTimeout, isMobile, isPopoverOpen, onClose]);

    const onDatepickerEvent = useCallback(
        (event: React.MouseEvent) => {
            const { target, type } = event;
            const isClickEvent = type === 'click';
            const isMouseMoveEvent = ['mousemove', 'mouseleave'].includes(type);
            const el = (target as HTMLElement)?.closest('.react-datepicker__day');

            if ((!isMobile && isClickEvent) || (isMobile && isMouseMoveEvent)) {
                return;
            }

            if (
                type === 'mouseleave' ||
                !el?.classList.contains('react-datepicker__day--disabled')
            ) {
                onClose();
                return;
            }

            const bounds = el.getBoundingClientRect();
            const newBounds = {
                top: bounds.top,
                left: bounds.left - 10, // fix offset issue
                x: bounds.x - 10, // fix offset issue
                y: bounds.y,
                width: bounds.width,
                height: bounds.height,
                bottom: bounds.bottom,
                right: bounds.right,
            };

            if (isEqual(newBounds, referenceBounds)) {
                return;
            }

            const dateEl = el.querySelector('.react-datepicker__day-inner');
            const timestamp = dateEl?.getAttribute('data-date');
            const date = timestamp ? new Date(+timestamp) : null;
            const dateString = date ? dateHelpers.format(date, RANGE_PICKER.DATE_FORMAT) : null;
            const tooltipText = dateString
                ? getTooltipText(
                      dateString,
                      dateRange?.startDate ?? null,
                      dateRange?.endDate ?? null,
                      daysConfig,
                      translationData,
                  )
                : undefined;

            setTooltipText(tooltipText);

            if (!tooltipText) {
                onClose();

                return;
            }

            setReferenceBounds({ ...newBounds });

            setIsPopoverOpen(true);
        },
        [
            dateRange?.endDate,
            dateRange?.startDate,
            daysConfig,
            isMobile,
            onClose,
            referenceBounds,
            translationData,
        ],
    );

    return {
        onDatepickerEvent,
        referenceBounds,
        onClose,
        isPopoverOpen,
        tooltipText,
    };
};
